import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, MessageCircleHeart, Pin, CheckCircle2, BookMarked, ChevronDown, Flame, Trash2, Copy, TrendingUp, TrendingDown, Minus, Settings2, Volume2, VolumeX, Mic, MicOff, Sparkles, X, Headphones } from 'lucide-react';
import PIELayout from './PIELayout';
import { pieApi, pieCoachSession, pieCoachPreseed } from './pieApi';
import PIEUnseededEmptyState, { isUnseededError } from './PIEUnseededEmptyState';
import './pie.css';
import logger from '../../lib/logger';
import authedFetch from '../../lib/authedFetch';
import { toast } from 'sonner';
import CoachAvatar from '../../components/coach/CoachAvatar';
import CoachPersonaPicker from '../../components/coach/CoachPersonaPicker';
import useCoachVoice from '../../hooks/useCoachVoice';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Heuristic: does this assistant message look like a multi-day plan?
 * Triggers the "📌 Save as my weekly plan" affordance. Conservative
 * by design — false positives clutter the chat with a button that
 * won't make sense.
 */
function looksLikePlan(text) {
  if (!text || text.length < 220) return false;
  const dayMatches = text.match(/\bDay\s*[1-7]\b/gi) || [];
  if (dayMatches.length >= 3) return true;
  // Numbered list with at least 3 entries from 1..7
  const nums = new Set();
  text.split(/\n/).forEach((line) => {
    const m = line.match(/^\s*([1-7])[.):-]\s+\S/);
    if (m) nums.add(m[1]);
  });
  return nums.size >= 3;
}

/**
 * PIECoachPage — Personal Impact Coach (Claude Sonnet 4.5).
 * Modern chat layout: assistant messages get a teal left-border
 * card, user turns get a softer right-aligned card. The "letter"
 * warmth lives in the COPY (system prompt), not in the layout.
 */
export default function PIECoachPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // If we landed via a "↗ where this came from" deep-link from
  // /me/validate, the URL carries `?session=<id>`. Rewire the active
  // session BEFORE we render so history loads from that exact thread.
  // We strip the param after consuming it so reloads behave normally.
  const sessionFromUrl = searchParams.get('session');
  if (sessionFromUrl && pieCoachSession.get() !== sessionFromUrl) {
    pieCoachSession.set(sessionFromUrl);
  }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [savingPlanIdx, setSavingPlanIdx] = useState(null);
  const [savedPlanIds, setSavedPlanIds] = useState({}); // {messageIdx: planId}
  const [actionStatus, setActionStatus] = useState(null); // transient toast text
  const sessionRef = useRef(pieCoachSession.get());
  const threadRef = useRef(null);
  const preseedFiredRef = useRef(false);

  // ── Coach persona + voice plumbing ──────────────────────────────
  const { user, viewAsOrg } = useAuth();
  // Auth payload exposes RBAC info under user.rbac (see AuthContext.js).
  // Org-admin gating must read from there — top-level user.role /
  // user.is_super_admin are not populated by /api/auth/me.
  const orgId = viewAsOrg?.id || user?.rbac?.organization_id || user?.organization_id;
  const isSuperAdmin = !!user?.rbac?.is_super_admin;
  const isOrgAdmin = isSuperAdmin
    || (user?.rbac?.roles || []).some((r) => r === 'org_admin' || r?.role === 'org_admin')
    || user?.role === 'org_admin';
  const [coachPrefs, setCoachPrefs] = useState(null); // resolved {persona_id, voice_uri, ...}
  const [personaCatalog, setPersonaCatalog] = useState({}); // {id: persona}
  const [pickerOpen, setPickerOpen] = useState(false);
  // Voice-first auto-submit confirmation chip: shows the captured
  // transcript for ~1.5s with a countdown bar; user can tap to bail.
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(null); // {text, startedAt}
  const AUTO_SUBMIT_DELAY_MS = 1500;
  // After this many uninterrupted voice-first turns, surface the
  // reflection summary card. Chosen so the wrap-up arrives when the
  // user is "warm" but hasn't yet drained the session of energy.
  const VOICE_FIRST_SUMMARY_THRESHOLD = 3;
  const voiceFirstTurnCountRef = useRef(0);
  // Card payload: {id, summary, turns, saved, ...}. Null = not surfaced.
  const [reflectionSummary, setReflectionSummary] = useState(null);
  const [savingReflection, setSavingReflection] = useState(false);
  const { speak, cancel, speaking, supported: voiceSupported } = useCoachVoice();

  // ── Speech-to-Text (microphone) ──────────────────────────────────
  // Zero-plugin native STT via window.SpeechRecognition. Mirrors the
  // TTS approach. Hidden gracefully on unsupported browsers.
  const inputBeforeListenRef = useRef('');
  // When the mic was auto-armed by voice-first mode (vs a manual tap)
  // the captured transcript should auto-submit on STT end. A ref keeps
  // this flag out of the render cycle so the effects below stay stable.
  const voiceFirstAutoMicRef = useRef(false);
  const prevSpeakingRef = useRef(false);
  const prevListeningRef = useRef(false);
  const latestInputRef = useRef(input);
  useEffect(() => { latestInputRef.current = input; }, [input]);

  const stt = useSpeechRecognition({ lang: 'en-US' });

  const startListening = useCallback((auto = false) => {
    if (!stt.supported || stt.listening || sending) return;
    voiceFirstAutoMicRef.current = !!auto;
    inputBeforeListenRef.current = latestInputRef.current;
    stt.start((text) => {
      const base = inputBeforeListenRef.current;
      const sep = base && !base.endsWith(' ') ? ' ' : '';
      setInput(`${base}${sep}${text}`);
    });
  }, [stt, sending]);

  const toggleMic = () => {
    if (!stt.supported) return;
    if (stt.listening) {
      // Manual stop cancels any pending auto-send so the user can edit
      // before pressing send themselves.
      voiceFirstAutoMicRef.current = false;
      stt.stop();
      return;
    }
    startListening(false);
  };

  // ── Voice-first loop ──────────────────────────────────────────────
  // 1) When the assistant's TTS finishes AND voice_first is ON, arm
  //    the mic automatically so the user can answer hands-free.
  useEffect(() => {
    const wasSpeaking = prevSpeakingRef.current;
    prevSpeakingRef.current = speaking;
    if (!wasSpeaking || speaking) return; // transition true→false only
    if (!coachPrefs?.voice_first) return;
    if (coachPrefs?.muted) return;
    if (!stt.supported || stt.listening || sending) return;
    // Small delay so the trailing tail of speech-end doesn't bleed
    // into the mic stream (the browser's TTS sometimes emits the onend
    // event a few ms before the audio output is fully silent).
    const t = setTimeout(() => startListening(true), 220);
    return () => clearTimeout(t);
  }, [speaking, coachPrefs?.voice_first, coachPrefs?.muted, stt.supported, stt.listening, sending, startListening]);

  // 2) When the STT session ends with content AND it was auto-armed by
  //    voice-first, surface a 1.5s confirmation chip so the user can
  //    bail/edit if the transcript is wrong. If they don't, the
  //    pendingAutoSubmit timer fires and we send the message.
  //    Manual mic sessions (toggleMic, voiceFirstAutoMicRef=false) are
  //    left to the user to send via Enter or the send button.
  useEffect(() => {
    const wasListening = prevListeningRef.current;
    prevListeningRef.current = stt.listening;
    if (!wasListening || stt.listening) return; // transition true→false only
    if (!voiceFirstAutoMicRef.current) return;
    voiceFirstAutoMicRef.current = false;
    const text = (latestInputRef.current || '').trim();
    if (!text) return;
    // Hand off to the confirmation chip — it starts its own countdown
    // and decides whether to actually submit.
    setPendingAutoSubmit({ text, startedAt: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stt.listening]);

  // 3) Voice-first confirmation timer. When pendingAutoSubmit exists,
  //    fire after AUTO_SUBMIT_DELAY_MS unless the user cancels (taps
  //    the chip or starts typing). Counts the voice-first turn on
  //    success so the reflection summary card can surface every N
  //    completed exchanges.
  useEffect(() => {
    if (!pendingAutoSubmit) return;
    const t = setTimeout(async () => {
      const text = (pendingAutoSubmit.text || '').trim();
      setPendingAutoSubmit(null);
      if (!text) return;
      const r = await sendMessage(text);
      if (!r) return;
      voiceFirstTurnCountRef.current += 1;
      // Don't double-trigger if a summary card is already on screen
      // — the user must dismiss/save it before the count starts again.
      if (
        voiceFirstTurnCountRef.current >= VOICE_FIRST_SUMMARY_THRESHOLD
        && !reflectionSummary
      ) {
        voiceFirstTurnCountRef.current = 0;
        try {
          const s = await pieApi.generateReflectionSummary(sessionRef.current);
          setReflectionSummary(s);
        } catch (e) {
          logger.warn('reflection summary fetch failed', e);
          // Fail-quiet — the hands-free loop continues normally.
        }
      }
    }, AUTO_SUBMIT_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoSubmit]);

  // Cancel the pending auto-submit so the user can edit before sending.
  // Also kills any in-flight TTS so the moment of correction is quiet.
  const cancelPendingAutoSubmit = useCallback(() => {
    setPendingAutoSubmit(null);
    if (speaking) cancel();
  }, [speaking, cancel]);

  // ── Reflection summary card handlers ──────────────────────────────
  // Save: promote draft → journal; Dismiss: drop without saving.
  // Both reset the voice-first turn counter so the card can re-surface
  // after another 3 hands-free exchanges.
  const saveReflectionToJournal = async () => {
    if (!reflectionSummary || savingReflection) return;
    setSavingReflection(true);
    try {
      const saved = await pieApi.saveReflectionSummary(reflectionSummary.id);
      setReflectionSummary({ ...reflectionSummary, ...saved });
      toast.success('Saved to your journal');
      // Brief pause so the success state is visible, then auto-close.
      setTimeout(() => setReflectionSummary(null), 1400);
    } catch (e) {
      logger.warn('save reflection summary failed', e);
      toast.error("Couldn't save just now. Try again.");
    } finally {
      setSavingReflection(false);
    }
  };
  const dismissReflection = () => {
    setReflectionSummary(null);
    if (speaking) cancel();
  };

  // Quick toolbar toggle for hands-free mode. Auto-enables auto_speak
  // when the user turns voice-first ON (so the assistant actually
  // speaks → closes the loop). Stops any in-flight mic session if the
  // user turns it OFF mid-conversation. Optimistic update is rolled
  // back on network failure so the pill never drifts from the server.
  const toggleVoiceFirst = async () => {
    const prev = coachPrefs;
    const next = !(coachPrefs?.voice_first);
    const patch = { voice_first: next };
    if (next && coachPrefs && !coachPrefs.auto_speak) patch.auto_speak = true;
    setCoachPrefs((p) => ({ ...(p || {}), ...patch }));
    if (!next) {
      voiceFirstAutoMicRef.current = false;
      if (stt.listening) stt.stop();
      // Also kill any pending auto-submit chip so the user isn't
      // surprised by a delayed send after they turned the mode off.
      setPendingAutoSubmit(null);
    }
    try {
      const r = await authedFetch(`${API}/api/coach/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (r.ok) {
        const d = await r.json();
        setCoachPrefs(d.resolved);
      } else {
        setCoachPrefs(prev);
        toast.error("Couldn't update hands-free mode. Try again.");
      }
    } catch (e) {
      logger.warn('voice-first toggle save failed', e);
      setCoachPrefs(prev);
      toast.error("Couldn't update hands-free mode. Check your connection.");
    }
  };

  // Load prefs + catalog once.
  useEffect(() => {
    (async () => {
      try {
        const [prefsRes, catalogRes] = await Promise.all([
          authedFetch(`${API}/api/coach/preferences`),
          authedFetch(`${API}/api/coach/personas`),
        ]);
        if (prefsRes.ok) setCoachPrefs(await prefsRes.json());
        if (catalogRes.ok) {
          const d = await catalogRes.json();
          const byId = {};
          (d.personas || []).forEach((p) => { byId[p.id] = p; });
          setPersonaCatalog(byId);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const activePersona = coachPrefs?.persona_id ? personaCatalog[coachPrefs.persona_id] : null;

  // Toggle the muted flag without opening the picker — quick header action.
  const toggleMute = async () => {
    const next = !(coachPrefs?.muted);
    setCoachPrefs((p) => ({ ...(p || {}), muted: next }));
    if (next && speaking) cancel();
    try {
      await authedFetch(`${API}/api/coach/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ muted: next }),
      });
    } catch { /* non-fatal */ }
  };

  // ── First-visit intro card helpers ────────────────────────────────
  // Persist that the user has seen the intro so it doesn't re-appear
  // on every login. Optionally also writes a persona_id when the user
  // accepts the industry-aware suggestion.
  const persistIntroDismissal = async ({ persona_id } = {}) => {
    const body = { first_visit_intro_dismissed: true };
    if (persona_id) body.persona_id = persona_id;
    try {
      const r = await authedFetch(`${API}/api/coach/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const d = await r.json();
        setCoachPrefs(d.resolved);
        if (persona_id && d.resolved?.persona_id === persona_id) {
          const name = personaCatalog[persona_id]?.name;
          toast.success(name ? `Coach set to ${name}` : 'Coach saved');
        }
      } else {
        // Optimistic local update so the card disappears, but surface a
        // toast so the user knows the choice didn't persist server-side.
        setCoachPrefs((p) => ({
          ...(p || {}),
          first_visit_intro_dismissed: true,
          ...(persona_id ? { persona_id, source: 'user' } : {}),
        }));
        toast.error("Couldn't save coach preference. We'll keep trying in the background.");
      }
    } catch (e) {
      logger.warn('intro dismissal save failed', e);
      setCoachPrefs((p) => ({
        ...(p || {}),
        first_visit_intro_dismissed: true,
        ...(persona_id ? { persona_id, source: 'user' } : {}),
      }));
      toast.error("Couldn't save coach preference. Check your connection.");
    }
  };

  const acceptSuggestedPersona = () => {
    const id = coachPrefs?.suggested_persona_id;
    if (!id) return;
    persistIntroDismissal({ persona_id: id });
  };
  const dismissIntro = () => persistIntroDismissal();
  const openPickerFromIntro = () => {
    persistIntroDismissal();
    setPickerOpen(true);
  };

  // Show the industry-aware intro only when the resolved persona came
  // from the app default (i.e. neither user nor org has chosen one)
  // AND the user hasn't already dismissed it.
  const showIntroCard = !!coachPrefs
    && coachPrefs.source === 'default'
    && !coachPrefs.first_visit_intro_dismissed;
  const suggestedPersona = coachPrefs?.suggested_persona_id
    ? personaCatalog[coachPrefs.suggested_persona_id]
    : null;

  useEffect(() => {
    // Strip the `?session=` param after we've consumed it so a manual
    // reload doesn't re-trigger the rewire on stale data.
    if (sessionFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete('session');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionRef.current) {
      pieApi
        .coachHistory(sessionRef.current)
        .then((d) => setMessages(d.messages || []))
        .catch((e) => {
          // Bubble up the unseeded-tenant signal so the page renders the
          // friendly empty state instead of the generic chat shell.
          const detail = e?.response?.data?.detail;
          if (detail && /no pie user found/i.test(detail)) setErr(detail);
          else logger.warn('coach history empty/failed', e);
        });
    } else {
      // No session yet — probe tenant readiness via a lightweight call
      // so deep-linking into /me/coach on a fresh deployment surfaces
      // the unseeded empty state instead of a half-loaded chat shell.
      pieApi
        .activePersona()
        .catch((e) => {
          const detail = e?.response?.data?.detail;
          if (detail && /no pie user found/i.test(detail)) setErr(detail);
        });
    }
  }, []);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const onSend = async (e) => {
    e?.preventDefault?.();
    return sendMessage(input);
  };

  /**
   * sendMessage — the actual send pipeline, callable both from the
   * form submit handler and from the pre-seed effect below.
   * Returns the response object (or null on failure) so callers like
   * the voice-first auto-submit can chain on completion (e.g. to
   * count turns and trigger the reflection-summary card).
   */
  const sendMessage = async (rawText) => {
    const text = (rawText || '').trim();
    if (!text || sending) return null;
    // If the mic is still hot when the user hits send, stop it
    // immediately. Otherwise SpeechRecognition keeps streaming and the
    // coach's TTS reply (when auto-speak is on) gets re-captured as
    // the user's next utterance. Also clear the voice-first auto-flag
    // so the in-flight transcript doesn't trigger a duplicate
    // confirmation chip after we've already submitted manually.
    if (stt.listening) stt.stop();
    voiceFirstAutoMicRef.current = false;
    setPendingAutoSubmit(null);
    setInput('');
    setSending(true);
    setErr(null);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    try {
      const r = await pieApi.coach(text, sessionRef.current);
      sessionRef.current = r.session_id;
      pieCoachSession.set(r.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: r.response }]);
      // Auto-speak the assistant reply if the user is opted-in and not muted.
      if (coachPrefs?.auto_speak && !coachPrefs?.muted && voiceSupported && r.response) {
        speak(r.response, {
          voiceUri: coachPrefs?.voice_uri,
          rate: coachPrefs?.voice_rate || 1.0,
          muted: false,
        });
      }
      return r;
    } catch (e2) {
      logger.error('coach send failed', e2);
      setErr(e2?.response?.data?.detail || 'Coach unavailable. Try again in a moment.');
      return null;
    } finally {
      setSending(false);
    }
  };

  // ── Pre-seed: if the user arrived via "Talk it over" we auto-send
  //    the seeded opener once on mount so the conversation starts
  //    grounded in the action they were considering.
  useEffect(() => {
    const seed = pieCoachPreseed.get();
    if (seed && !preseedFiredRef.current && messages.length === 0) {
      preseedFiredRef.current = true;
      pieCoachPreseed.clear();
      // Tiny delay so the page entrance animation finishes first.
      setTimeout(() => sendMessage(seed), 320);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  /**
   * Save a Coach-generated plan as a structured weekly commitment.
   * Backend uses Claude to parse the free-form plan into 7 days,
   * persists it, then we route the user to /me/dashboard where the
   * "This week's plan" card now renders with checkable rows.
   */
  const onSavePlan = async (idx, content) => {
    if (savingPlanIdx !== null) return;
    setSavingPlanIdx(idx);
    try {
      const saved = await pieApi.saveWeeklyPlan(content, sessionRef.current);
      setSavedPlanIds((prev) => ({ ...prev, [idx]: saved.id }));
      // Brief pause so the "Saved" affirmation registers, then route home.
      setTimeout(() => navigate('/mirror/dashboard'), 700);
    } catch (e2) {
      logger.error('save plan failed', e2);
      setErr(e2?.response?.data?.detail || 'Could not save plan. Try again.');
      setSavingPlanIdx(null);
    }
  };

  /** Clear the on-screen conversation and start a fresh session. The
   *  prior turns remain in the database for audit/coach memory; we
   *  just disconnect the current UI from them so the user gets a
   *  clean slate. */
  const onClearChat = () => {
    if (sending) return;
    if (messages.length > 0) {
      const ok = window.confirm("Clear this conversation? Your reflections from prior weeks stay safe — only this thread will be reset.");
      if (!ok) return;
    }
    setMessages([]);
    setSavedPlanIds({});
    setErr(null);
    pieCoachSession.clear();
    sessionRef.current = null;
    setActionStatus('Cleared. Fresh session started.');
    setTimeout(() => setActionStatus(null), 2400);
  };

  /** Copy the conversation as plain text so the user can paste it
   *  into a journal, doc, or message later. Format: alternating
   *  "You:" / "Coach:" with blank lines between turns. */
  const onCopyChat = async () => {
    if (messages.length === 0) {
      setActionStatus('Nothing to copy yet.');
      setTimeout(() => setActionStatus(null), 1800);
      return;
    }
    const lines = messages.map((m) => `${m.role === 'user' ? 'You' : 'Coach'}: ${m.content}`).join('\n\n');
    const stamp = new Date().toLocaleString();
    const blob = `Seertech Mirror Coach — ${stamp}\n\n${lines}\n`;
    try {
      await navigator.clipboard.writeText(blob);
      setActionStatus(`Copied ${messages.length} message${messages.length === 1 ? '' : 's'} to your clipboard.`);
    } catch (e) {
      logger.warn('clipboard copy failed', e);
      setActionStatus('Could not access clipboard. Try selecting + copying manually.');
    }
    setTimeout(() => setActionStatus(null), 2800);
  };

  return (
    <PIELayout narrow>
      {isUnseededError(err) ? <PIEUnseededEmptyState /> : (
      <div className="pie-fade-in" data-testid="pie-coach">
        <section className="pie-hero-center">
          <span className="pie-eyebrow">
            <MessageCircleHeart size={12} /> Coach
          </span>
          <h1 className="pie-hero-h1">
            Tell me what&apos;s on <span className="pie-accent-text">your mind</span>.
          </h1>
          <p className="pie-hero-sub">
            I&apos;ve got your numbers in front of me — capability, motivation, what you&apos;re shipping,
            what&apos;s sticking. Ask me anything. I&apos;ll be straight with you.
          </p>
          <CoachMemoryChip />
        </section>

        <div className="pie-coach-toolbar" data-testid="pie-coach-toolbar">
          <div className="pie-coach-toolbar-left">
            {activePersona && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-600" data-testid="pie-coach-active-persona">
                <CoachAvatar persona={activePersona} size={28} />
                <span className="font-semibold">{activePersona.name}</span>
                <span className="text-slate-400">·</span>
                <span className="capitalize">{activePersona.tone}</span>
              </span>
            )}
            {actionStatus && (
              <span className="pie-coach-toolbar-status" data-testid="pie-coach-toolbar-status">
                {actionStatus}
              </span>
            )}
          </div>
          <div className="pie-coach-toolbar-right">
            {stt.supported && (
              <button
                type="button"
                className={`pie-coach-toolbar-btn ${coachPrefs?.voice_first ? 'pie-coach-toolbar-btn-on' : ''}`}
                onClick={toggleVoiceFirst}
                data-testid="pie-coach-voice-first"
                title={coachPrefs?.voice_first ? 'Turn off hands-free' : 'Hands-free: mic auto-arms after each reply'}
                aria-pressed={!!coachPrefs?.voice_first}
              >
                <Headphones size={12} /> {coachPrefs?.voice_first ? 'Hands-free on' : 'Hands-free'}
              </button>
            )}
            <button
              type="button"
              className="pie-coach-toolbar-btn"
              onClick={toggleMute}
              data-testid="pie-coach-mute"
              title={coachPrefs?.muted ? 'Unmute voice' : 'Mute voice'}
              aria-label={coachPrefs?.muted ? 'Unmute voice' : 'Mute voice'}
            >
              {coachPrefs?.muted
                ? <><VolumeX size={12} /> Muted</>
                : <><Volume2 size={12} /> Voice on</>}
            </button>
            <button
              type="button"
              className="pie-coach-toolbar-btn"
              onClick={() => setPickerOpen(true)}
              data-testid="pie-coach-settings"
              title="Choose your coach + voice"
            >
              <Settings2 size={12} /> Coach settings
            </button>
            <button
              type="button"
              className="pie-coach-toolbar-btn"
              onClick={onCopyChat}
              disabled={messages.length === 0}
              data-testid="pie-coach-copy"
              title="Copy this conversation as plain text"
            >
              <Copy size={12} /> Copy chat
            </button>
            <button
              type="button"
              className="pie-coach-toolbar-btn pie-coach-toolbar-btn-danger"
              onClick={onClearChat}
              disabled={messages.length === 0 || sending}
              data-testid="pie-coach-clear"
              title="Clear this conversation and start fresh"
            >
              <Trash2 size={12} /> Clear chat
            </button>
          </div>
        </div>

        <div
          ref={threadRef}
          className="pie-coach-thread"
          style={{ minHeight: 280, maxHeight: '60vh', overflowY: 'auto' }}
          data-testid="pie-coach-thread"
        >
          <SessionVerdictBanner session_id={sessionRef.current} />
          {showIntroCard && (
            <CoachIntroCard
              suggested={suggestedPersona}
              onAccept={acceptSuggestedPersona}
              onPickAnother={openPickerFromIntro}
              onDismiss={dismissIntro}
            />
          )}
          {messages.length === 0 && !sending && (
            <CoachEmptyState onPickPrompt={(text) => sendMessage(text)} />
          )}
          {messages.map((m, i) =>
            m.role === 'user' ? (
              <div key={i} className="pie-coach-turn-user" data-testid={`pie-coach-msg-user-${i}`}>
                {m.content}
              </div>
            ) : (
              <div key={i}>
                <div className="pie-coach-turn-assistant" data-testid={`pie-coach-msg-assistant-${i}`}>
                  {activePersona && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <CoachAvatar persona={activePersona} size={32} />
                      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pie-text-muted)' }}>
                        {activePersona.name}
                      </span>
                      {voiceSupported && !coachPrefs?.muted && coachPrefs?.voice_uri && (
                        <button
                          type="button"
                          onClick={() => speak(m.content, {
                            voiceUri: coachPrefs.voice_uri,
                            rate: coachPrefs.voice_rate || 1.0,
                            muted: false,
                          })}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--pie-text-muted)', padding: 2 }}
                          data-testid={`pie-coach-speak-${i}`}
                          title="Read this reply aloud"
                          aria-label="Read this reply aloud"
                        >
                          <Volume2 size={12} />
                        </button>
                      )}
                    </div>
                  )}
                  <CoachMarkdown text={m.content} />
                </div>
                {looksLikePlan(m.content) && (
                  <div className="pie-coach-plan-save-bar" data-testid={`pie-coach-plan-save-bar-${i}`}>
                    {savedPlanIds[i] ? (
                      <span className="pie-coach-plan-saved" data-testid={`pie-coach-plan-saved-${i}`}>
                        <CheckCircle2 size={13} /> Saved to your dashboard. Routing you there…
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="pie-btn pie-btn-coach-this pie-btn-save-plan"
                        onClick={() => onSavePlan(i, m.content)}
                        disabled={savingPlanIdx !== null}
                        data-testid={`pie-coach-save-plan-${i}`}
                        title="Parse this into 7 checkable days and pin it to your dashboard"
                      >
                        <Pin size={13} />
                        {savingPlanIdx === i ? 'Saving…' : 'Save as my weekly plan'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ),
          )}
          {sending && (
            <div style={{ color: 'var(--pie-text-muted)', fontSize: 14, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
              <span className="pie-loader"><span /><span /><span /></span>
              <span>Thinking…</span>
            </div>
          )}
          {err && (
            <div className="pie-card" style={{ color: 'var(--pie-danger)' }} data-testid="pie-coach-error">
              {err}
            </div>
          )}
        </div>

        {reflectionSummary && (
          <ReflectionSummaryCard
            data={reflectionSummary}
            persona={activePersona}
            voiceSupported={voiceSupported}
            muted={!!coachPrefs?.muted}
            voiceUri={coachPrefs?.voice_uri}
            voiceRate={coachPrefs?.voice_rate || 1.0}
            speak={speak}
            cancel={cancel}
            speaking={speaking}
            saving={savingReflection}
            onSave={saveReflectionToJournal}
            onDismiss={dismissReflection}
          />
        )}

        {pendingAutoSubmit && (
          <VoiceFirstConfirmChip
            text={pendingAutoSubmit.text}
            delayMs={AUTO_SUBMIT_DELAY_MS}
            onCancel={cancelPendingAutoSubmit}
          />
        )}

        <form onSubmit={onSend} className="pie-coach-input-wrap" data-testid="pie-coach-form">
          <textarea
            className="pie-coach-input"
            placeholder={stt.listening ? 'Listening… speak naturally.' : 'Type freely. The coach is listening.'}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              // Any keystroke during the confirmation window means the
              // user is correcting — route through cancelPendingAutoSubmit
              // so any in-flight TTS is also silenced consistently.
              if (pendingAutoSubmit) cancelPendingAutoSubmit();
            }}
            onKeyDown={onKey}
            disabled={sending}
            rows={2}
            data-testid="pie-coach-input"
          />
          {stt.supported && (
            <button
              type="button"
              onClick={toggleMic}
              disabled={sending}
              className={`pie-coach-mic-btn ${stt.listening ? 'pie-coach-mic-btn-on' : ''}`}
              data-testid="pie-coach-mic"
              title={stt.listening ? 'Stop listening' : 'Speak to the coach'}
              aria-label={stt.listening ? 'Stop listening' : 'Speak to the coach'}
              aria-pressed={stt.listening}
            >
              {stt.listening ? <MicOff size={14} /> : <Mic size={14} />}
            </button>
          )}
          <button
            type="submit"
            className="pie-btn pie-btn-primary"
            disabled={sending || !input.trim()}
            data-testid="pie-coach-send"
            style={{ padding: '10px 16px' }}
          >
            <Send size={14} />
          </button>
        </form>
      </div>
      )}
      <CoachPersonaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        currentPrefs={coachPrefs}
        currentOrgId={orgId}
        isOrgAdmin={isOrgAdmin}
        onSaved={(resolved) => {
          setCoachPrefs(resolved);
          // Test the new voice immediately so the user gets feedback.
          if (resolved && !resolved.muted && resolved.voice_uri && voiceSupported) {
            speak("Voice set. I'm ready when you are.", {
              voiceUri: resolved.voice_uri, rate: resolved.voice_rate, muted: false,
            });
          }
        }}
      />
    </PIELayout>
  );
}

/**
 * CoachEmptyState — first-visit landing inside the chat thread.
 * Pulls a personalised greeting from the backend (composed from the
 * persona name + the user's most recent retrospective) and surfaces a
 * dynamic continuity-prompt chip alongside the three generic starters.
 * If the welcome endpoint fails for any reason we fall back to the
 * static "blank thread" copy so the empty state is never broken.
 */
function CoachEmptyState({ onPickPrompt }) {
  const [welcome, setWelcome] = useState(null);

  useEffect(() => {
    let mounted = true;
    pieApi
      .coachWelcome()
      .then((d) => { if (mounted) setWelcome(d); })
      .catch(() => { /* fall through to static copy */ });
    return () => { mounted = false; };
  }, []);

  // Continuity prompt = a fast "yes, keep going" chip when the welcome
  // endpoint detected a recent thread. Surfaces ABOVE the three
  // generic starter prompts so it's the obvious next step.
  const continuityPrompt = welcome?.kind === 'continuity'
    ? "Yes — keep us on that thread. What should I do this week to keep the momentum?"
    : welcome?.kind === 'pivot'
      ? "Yes — help me figure out what to try instead."
      : null;
  const prompts = [
    "What's the one thing I should focus on this week?",
    "Why is my motivation lower than usual?",
    "Help me draft a Friday wrap-up of how this week went.",
  ];

  const headline = welcome?.greeting
    || "A blank thread. A grounded coach.";
  const body = welcome
    ? null
    : (
      <>
        I&apos;ve already loaded your numbers and your last few wrap-ups. Pick a starter
        below — or just tell me what&apos;s on your mind.
      </>
    );

  return (
    <div className="pie-coach-empty" data-testid="pie-coach-empty">
      <div className="pie-coach-empty-icon" aria-hidden>
        <MessageCircleHeart size={20} />
      </div>
      <div className="pie-coach-empty-headline" data-testid="pie-coach-empty-greeting">
        {headline}
      </div>
      {body && <div className="pie-coach-empty-body">{body}</div>}
      <div className="pie-coach-empty-prompts">
        {continuityPrompt && (
          <button
            type="button"
            className="pie-coach-empty-chip pie-coach-empty-chip-primary"
            onClick={() => onPickPrompt(continuityPrompt)}
            data-testid="pie-coach-empty-chip-continuity"
          >
            {continuityPrompt}
          </button>
        )}
        {prompts.map((p, i) => (
          <button
            key={i}
            type="button"
            className="pie-coach-empty-chip"
            onClick={() => onPickPrompt(p)}
            data-testid={`pie-coach-empty-chip-${i}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * CoachMemoryChip — a small "What I remember about you" affordance
 * placed under the Coach hero. Clicking it expands an inline panel
 * showing the user's last 3 weekly retrospectives (completion %, what
 * stuck, what didn't, when it was wrapped up). Builds the credibility
 * flywheel: the user can SEE the trail the Coach is grounded in,
 * which makes them more willing to be honest in the next reflection.
 */
function CoachMemoryChip() {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([pieApi.weeklyPlanHistory(), pieApi.weeklyPlanStreak()])
      .then(([h, s]) => {
        if (!mounted) return;
        const completed = (h.plans || []).filter((p) => p.status === 'completed' && p.retrospective);
        setHistory(completed.slice(0, 3));
        setStreak(s);
      })
      .catch((e) => {
        logger.warn('coach memory load failed', e);
        if (mounted) {
          setHistory([]);
          setStreak(null);
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || history === null) return null;

  const count = history.length;
  const labelCopy = count === 0
    ? 'No memory yet — wrap up a week to teach me'
    : count === 1
      ? 'I remember 1 prior week'
      : `I remember ${count} prior weeks`;

  return (
    <div className="pie-coach-memory-wrap" data-testid="pie-coach-memory">
      <div className="pie-coach-memory-row">
        <button
          type="button"
          className="pie-coach-memory-chip"
          onClick={() => count > 0 && setOpen((v) => !v)}
          aria-expanded={open}
          disabled={count === 0}
          data-testid="pie-coach-memory-chip"
          title={count === 0 ? 'No memory yet' : 'See what I remember'}
        >
          <BookMarked size={12} />
          <span>{labelCopy}</span>
          {count > 0 && (
            <ChevronDown
              size={12}
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }}
            />
          )}
        </button>
        {streak && streak.current_streak > 0 && (
          <span className="pie-streak-pill" data-testid="pie-coach-streak-pill">
            <Flame size={12} aria-hidden />
            <span>{streak.current_streak}-week streak</span>
          </span>
        )}
      </div>
      {open && count > 0 && (
        <div className="pie-coach-memory-panel" data-testid="pie-coach-memory-panel">
          <div className="pie-coach-memory-panel-eyebrow">
            What I&apos;m grounded in (your own words):
          </div>
          {history.map((p) => (
            <MemoryCard key={p.id} plan={p} />
          ))}
          <div className="pie-coach-memory-panel-foot">
            Every coach reply privately references these. Honest reflections → smarter advice.
          </div>
        </div>
      )}
    </div>
  );
}

function MemoryCard({ plan }) {
  const retro = plan.retrospective || {};
  const cr = Math.round((retro.completion_rate || 0) * 100);
  const date = retro.submitted_at || plan.completed_at || plan.created_at || '';
  const dateStr = date ? new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  return (
    <div className="pie-coach-memory-card" data-testid={`pie-coach-memory-card-${plan.id}`}>
      <div className="pie-coach-memory-card-head">
        <span className="pie-mono pie-coach-memory-pct">{cr}%</span>
        <span className="pie-coach-memory-card-title">{plan.plan_title || 'Week'}</span>
        <span className="pie-coach-memory-date">{dateStr}</span>
      </div>
      {retro.what_stuck && (
        <div className="pie-coach-memory-line">
          <span className="pie-coach-memory-line-label">stuck</span>
          <span>“{retro.what_stuck}”</span>
        </div>
      )}
      {retro.what_didnt && (
        <div className="pie-coach-memory-line pie-coach-memory-line-didnt">
          <span className="pie-coach-memory-line-label">didn&apos;t</span>
          <span>“{retro.what_didnt}”</span>
        </div>
      )}
    </div>
  );
}

/**
 * SessionVerdictBanner — when the user lands on a coaching thread
 * that birthed a now-validated commitment, frame the conversation as
 * a tested hypothesis: "PS — you came back to validate this. Realised
 * ROE 72% vs predicted 64%." Hidden when no verdicts exist (i.e., the
 * normal mid-conversation case). Fail-silent on API error.
 *
 * Lives ABOVE the chat thread so it sets context before the user
 * reads any messages.
 */
function SessionVerdictBanner({ session_id }) {
  const [verdicts, setVerdicts] = useState(null);
  useEffect(() => {
    if (!session_id) return undefined;
    let mounted = true;
    pieApi
      .coachSessionVerdict(session_id)
      .then((d) => { if (mounted) setVerdicts(d.validated || []); })
      .catch(() => { if (mounted) setVerdicts([]); });
    return () => { mounted = false; };
  }, [session_id]);

  if (!verdicts || verdicts.length === 0) return null;

  return (
    <div className="pie-coach-verdict" data-testid="pie-coach-session-verdict">
      <div className="pie-coach-verdict-eyebrow pie-mono">
        PS — you came back to validate this
      </div>
      <ul className="pie-coach-verdict-list">
        {verdicts.map((v) => {
          const Icon = v.verdict === 'better' ? TrendingUp : v.verdict === 'worse' ? TrendingDown : Minus;
          const tone = v.verdict === 'better' ? 'up' : v.verdict === 'worse' ? 'down' : 'flat';
          return (
            <li
              key={v.commitment_id}
              className="pie-coach-verdict-row"
              data-testid={`pie-coach-verdict-${v.commitment_id}`}
            >
              <span className={`pie-coach-verdict-icon pie-coach-verdict-icon-${tone}`} aria-hidden>
                <Icon size={13} />
              </span>
              <span className="pie-coach-verdict-action">{v.action_label}</span>
              <span className="pie-coach-verdict-roe pie-mono">
                Realised <strong>{v.realised_roe_pct}%</strong>
                <span className="pie-coach-verdict-vs"> vs predicted {v.predicted_roe_pct}%</span>
                <span className={`pie-coach-verdict-diff pie-coach-verdict-diff-${tone}`}>
                  {v.diff_pp > 0 ? '+' : ''}{v.diff_pp}pp
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}



/**
 * CoachIntroCard — first-visit "Meet your coach" intro.
 * Surfaces an industry-aware persona suggestion when the resolved
 * coach is still the app default. The user can accept the suggestion
 * (one click → sets it as their persona) or pick a different one,
 * which opens the full picker. Either action dismisses the card
 * permanently for that user so it never returns on subsequent logins.
 *
 * Fail-soft: if no suggested persona was returned (e.g. the org has no
 * industry tagged) we render a generic "Pick your coach" prompt so
 * the card still serves its onboarding purpose.
 */
function CoachIntroCard({ suggested, onAccept, onPickAnother, onDismiss }) {
  return (
    <div className="pie-coach-intro-card" data-testid="pie-coach-intro-card">
      <button
        type="button"
        className="pie-coach-intro-close"
        onClick={onDismiss}
        aria-label="Dismiss intro"
        data-testid="pie-coach-intro-dismiss"
      >
        <X size={14} />
      </button>
      <div className="pie-coach-intro-eyebrow">
        <Sparkles size={12} /> Meet your coach
      </div>
      {suggested ? (
        <>
          <div className="pie-coach-intro-row">
            <CoachAvatar persona={suggested} size={72} />
            <div className="pie-coach-intro-text">
              <div className="pie-coach-intro-name" data-testid="pie-coach-intro-name">
                {suggested.name}
                <span className="pie-coach-intro-tone"> · {suggested.tone}</span>
              </div>
              <p className="pie-coach-intro-tagline">{suggested.tagline}</p>
              <p className="pie-coach-intro-foot">
                Suggested based on your organization&apos;s industry. You can change this anytime.
              </p>
            </div>
          </div>
          <div className="pie-coach-intro-actions">
            <button
              type="button"
              className="pie-btn pie-btn-primary"
              onClick={onAccept}
              data-testid="pie-coach-intro-accept"
            >
              Use this coach
            </button>
            <button
              type="button"
              className="pie-coach-toolbar-btn"
              onClick={onPickAnother}
              data-testid="pie-coach-intro-pick-another"
            >
              Pick another
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="pie-coach-intro-tagline" data-testid="pie-coach-intro-generic">
            Choose a coach voice and style that fits how you like to be challenged.
            You can change this anytime.
          </p>
          <div className="pie-coach-intro-actions">
            <button
              type="button"
              className="pie-btn pie-btn-primary"
              onClick={onPickAnother}
              data-testid="pie-coach-intro-pick"
            >
              Pick a coach
            </button>
            <button
              type="button"
              className="pie-coach-toolbar-btn"
              onClick={onDismiss}
              data-testid="pie-coach-intro-skip"
            >
              Maybe later
            </button>
          </div>
        </>
      )}
    </div>
  );
}


/**
 * VoiceFirstConfirmChip — 1.5-second bail-out for hands-free mode.
 *
 * After STT captures speech in voice-first mode, this chip flashes
 * the transcript with a thin progress bar counting down to auto-submit.
 * Tapping the chip cancels the timer (the parent clears
 * pendingAutoSubmit) so the user can edit the textarea before sending.
 *
 * Stateless beyond its own animation tick — the actual setTimeout that
 * fires send lives in PIECoachPage so cancellation is a single
 * setState call. We only render the visual countdown here.
 */
function VoiceFirstConfirmChip({ text, delayMs, onCancel }) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startedAt = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, 1 - elapsed / delayMs);
      setProgress(remaining * 100);
      if (remaining > 0) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [delayMs]);

  // Trim very long transcripts so the chip doesn't blow up vertically
  // — we only need enough to recognise the utterance, not re-read it.
  const preview = text.length > 140 ? `${text.slice(0, 137)}…` : text;

  return (
    <button
      type="button"
      className="pie-coach-confirm-chip"
      onClick={onCancel}
      data-testid="pie-coach-confirm-chip"
      title="Tap to edit before sending"
      aria-label={`You said "${preview}". Tap to edit or wait to send.`}
      role="status"
      aria-live="polite"
    >
      <div className="pie-coach-confirm-chip-body">
        <span className="pie-coach-confirm-chip-eyebrow">You said</span>
        <span className="pie-coach-confirm-chip-text" data-testid="pie-coach-confirm-chip-text">
          “{preview}”
        </span>
        <span className="pie-coach-confirm-chip-hint">Tap to edit · sending in a moment</span>
      </div>
      <div
        className="pie-coach-confirm-chip-bar"
        aria-hidden
        style={{ width: `${progress}%` }}
      />
    </button>
  );
}


/**
 * ReflectionSummaryCard — voice-first wrap-up card.
 *
 * Surfaces after VOICE_FIRST_SUMMARY_THRESHOLD hands-free turns with a
 * persona-voiced 20-second narration of "what the coach is hearing".
 * Auto-speaks on mount (when voice is on + not muted) so commute-end
 * users get the summary without lifting a finger; one tap saves to
 * their journal, one tap dismisses.
 *
 * Self-contained so it doesn't add to the page-level effect graph.
 */
function ReflectionSummaryCard({
  data, persona, voiceSupported, muted, voiceUri, voiceRate,
  speak, cancel, speaking, saving, onSave, onDismiss,
}) {
  // Auto-speak the summary once when the card mounts. We DON'T re-fire
  // on prop changes — that would replay every time the parent
  // re-renders. The user can re-play via the speaker button below.
  const spokenRef = useRef(false);
  useEffect(() => {
    if (spokenRef.current) return;
    spokenRef.current = true;
    if (!voiceSupported || muted || !data?.summary) return;
    speak(data.summary, { voiceUri, rate: voiceRate, muted: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReplay = () => {
    if (!data?.summary) return;
    if (speaking) cancel();
    speak(data.summary, { voiceUri, rate: voiceRate, muted: false });
  };
  const saved = !!data?.saved;

  return (
    <div
      className="pie-coach-reflection-card"
      data-testid="pie-coach-reflection-card"
      role="region"
      aria-live="polite"
    >
      <button
        type="button"
        className="pie-coach-reflection-close"
        onClick={onDismiss}
        data-testid="pie-coach-reflection-dismiss"
        aria-label="Dismiss reflection summary"
      >
        <X size={14} />
      </button>
      <div className="pie-coach-reflection-eyebrow">
        <Sparkles size={12} /> Reflection · {data?.turns ? `${data.turns} turns` : 'just now'}
      </div>
      <div className="pie-coach-reflection-row">
        {persona && <CoachAvatar persona={persona} size={48} />}
        <p className="pie-coach-reflection-text" data-testid="pie-coach-reflection-text">
          {data?.summary}
        </p>
      </div>
      <div className="pie-coach-reflection-actions">
        {voiceSupported && !muted && (
          <button
            type="button"
            className="pie-coach-toolbar-btn"
            onClick={onReplay}
            data-testid="pie-coach-reflection-replay"
            title="Replay the summary"
            aria-label="Replay the summary"
          >
            <Volume2 size={12} /> {speaking ? 'Playing…' : 'Replay'}
          </button>
        )}
        <button
          type="button"
          className="pie-btn pie-btn-primary"
          onClick={onSave}
          disabled={saving || saved}
          data-testid="pie-coach-reflection-save"
        >
          {saved
            ? <><CheckCircle2 size={13} /> Saved</>
            : <><BookMarked size={13} /> {saving ? 'Saving…' : 'Save to journal'}</>}
        </button>
      </div>
    </div>
  );
}



/**
 * CoachMarkdown — minimal, safe markdown renderer for assistant
 * messages. The coach LLM emits Markdown-flavoured prose
 * (`**bold**`, `*italic*`, `` `code` ``, bullet lists, line breaks)
 * but we don't want to pull in a full markdown engine for what is
 * essentially conversational text. This walks the string with a
 * small inline tokenizer + a paragraph/list block parser.
 *
 * Safety: we never use `dangerouslySetInnerHTML`. Every leaf is a
 * React text node so user-supplied content can't inject markup.
 *
 * Supported (intentionally narrow):
 *  - `**bold**`, `__bold__`
 *  - `*italic*`, `_italic_`
 *  - `` `code` `` (inline)
 *  - Bullet lists ("- " or "* " or "• " line prefix)
 *  - Numbered lists ("1. ", "2. " …)
 *  - Blank-line separated paragraphs
 *
 * Not supported (by design): links (LLM rarely emits raw URLs here;
 * we'd want auto-linkification with strict allowlist before turning
 * those into clickable anchors), headings, blockquotes, images.
 */
function CoachMarkdown({ text }) {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  const blocks = [];
  let buf = [];
  let listKind = null; // 'ul' | 'ol' | null

  const flushParagraph = () => {
    if (!buf.length) return;
    blocks.push({ type: 'p', content: buf.join(' ') });
    buf = [];
  };
  const flushList = () => {
    if (!listKind || !buf.length) return;
    blocks.push({ type: listKind, items: [...buf] });
    buf = [];
    listKind = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      // Blank line — boundary between blocks.
      flushParagraph();
      flushList();
      continue;
    }
    const ulMatch = line.match(/^[-*•]\s+(.*)$/);
    const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (ulMatch) {
      if (listKind && listKind !== 'ul') flushList();
      flushParagraph();
      listKind = 'ul';
      buf.push(ulMatch[1]);
      continue;
    }
    if (olMatch) {
      if (listKind && listKind !== 'ol') flushList();
      flushParagraph();
      listKind = 'ol';
      buf.push(olMatch[2]);
      continue;
    }
    // Plain line — closes any active list, accumulates paragraph.
    if (listKind) flushList();
    buf.push(line);
  }
  flushParagraph();
  flushList();

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'p') {
          return <p key={i} className="pie-coach-md-p"><Inline text={b.content} /></p>;
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="pie-coach-md-ul">
              {b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}
            </ul>
          );
        }
        return (
          <ol key={i} className="pie-coach-md-ol">
            {b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}
          </ol>
        );
      })}
    </>
  );
}

/**
 * Inline — tokenises a single line/paragraph for bold/italic/code
 * spans. Greedy outer→inner: code (`…`) wins first so we never try
 * to parse markdown inside literal code spans.
 */
function Inline({ text }) {
  // First pass: split on backtick code spans so they're never re-parsed.
  const parts = String(text).split(/(`[^`]+`)/g);
  return parts.map((seg, i) => {
    if (seg.startsWith('`') && seg.endsWith('`') && seg.length >= 2) {
      return <code key={i} className="pie-coach-md-code">{seg.slice(1, -1)}</code>;
    }
    return <InlineEmphasis key={i} text={seg} />;
  });
}

/**
 * InlineEmphasis — handles **bold**, __bold__, *italic*, _italic_.
 * Order matters: bold tokens are double-character and must be
 * checked before the single-character italic tokens.
 */
function InlineEmphasis({ text }) {
  // Combined regex captures bold (**…** or __…__) and italic
  // (*…* or _…_). We deliberately disallow whitespace adjacent to
  // markers so prose like "5 * 3" isn't mistaken for italics.
  const re = /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|(?<![\w*])\*(?!\s)([^*\n]+?)\*(?!\w)|(?<![\w_])_(?!\s)([^_\n]+?)_(?!\w))/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**') || tok.startsWith('__')) {
      out.push(<strong key={`b-${m.index}`}>{tok.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={`i-${m.index}`}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}
